import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { fadeIn, scaleHover } from '@/animations/variants';
import Button from './Button';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Industries', href: '/industries' },
  { name: 'Technology', href: '/technology' },
  { name: 'Insights', href: '/insights' },
  { name: 'Contact', href: '/contact' },
];

export default function Header() {
    // Scroll to top and close mobile menu on navigation
    const handleNavClick = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    };
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <motion.header
      className="bg-background/90 backdrop-blur-md border-b border-border sticky top-0 z-50 shadow-sm"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <motion.div 
            className="flex-shrink-0"
            variants={fadeIn}
          >
            <Link to="/" className="flex items-center">
              {/* Short logo for mobile and tablet */}
              <img 
                src="/images/EB Logo.png"
                alt="EB Logo" 
                className="h-10 sm:h-14 w-auto mr-2 block lg:hidden" 
                style={{ maxHeight: '3rem' }}
              />
              {/* Long logo for laptop and desktop */}
              <img 
                src="/images/Ennobridge LOGO 3.png" 
                alt="Ennobridge Logo" 
                className="h-6 sm:h-8 w-auto mr-2 hidden lg:block" 
                style={{ maxHeight: '2rem' }}
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:block">
            <div className="flex items-baseline space-x-2 xl:space-x-4">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <motion.div
                    key={item.name}
                    variants={scaleHover}
                    whileHover="whileHover"
                  >
                    <Link
                      to={item.href}
                      onClick={handleNavClick}
                      className={`
                        px-2 xl:px-3 py-2 rounded-md text-sm font-body transition-all duration-300
                        ${isActive 
                          ? 'text-[color:#162660] bg-primary/10 border border-primary/20' 
                          : 'text-[color:#162660] hover:text-[color:#162660] hover:bg-surface/50'
                        }
                      `}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <a
              href="https://wa.me/919270161536?text=Hi%20Ennobridge%20Team!%20%F0%9F%91%8B%0A%0AI%20came%20across%20your%20website%20and%20I%27m%20interested%20in%20discussing%20a%20project.%20I%27d%20love%20to%20learn%20more%20about%20your%20services%20and%20how%20you%20can%20help%20with%20my%20requirements.%0A%0ALooking%20forward%20to%20connecting%20with%20you!"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Button size="sm" className="flex items-center gap-2 text-xs xl:text-sm">
                <span className="hidden xl:inline-flex items-center gap-1">
                  <svg className="text-green-500 w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-label="WhatsApp">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.251"/>
                  </svg>
                  <span className="font-semibold">Whatsapp</span>
                </span>
                <span className="xl:hidden">Contact</span>
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-textSecondary hover:text-textPrimary hover:bg-surface/80 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <motion.div 
            className="lg:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-surface/95 backdrop-blur-sm rounded-lg mt-2 border border-border/50 shadow-lg">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={`
                      block px-3 py-2 rounded-md text-base font-medium transition-colors
                      ${isActive 
                        ? 'text-primary bg-primary/10' 
                        : 'text-textSecondary hover:text-textPrimary hover:bg-surface'
                      }
                    `}
                  >
                    {item.name}
                  </Link>
                );
              })}
              <div className="pt-4">
                <a
                  href="https://wa.me/919270161536?text=Hi%20Ennobridge%20Team!%20%F0%9F%91%8B%0A%0AI%20came%20across%20your%20website%20and%20I%27m%20interested%20in%20discussing%20a%20project.%20I%27d%20love%20to%20learn%20more%20about%20your%20services%20and%20how%20you%20can%20help%20with%20my%20requirements.%0A%0ALooking%20forward%20to%20connecting%20with%20you!"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button fullWidth size="sm" className="flex items-center justify-center gap-2 text-base">
                    <svg className="text-green-500 w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-label="WhatsApp">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.251"/>
                    </svg>
                    <span className="font-semibold">WhatsApp</span>
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
}